import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Mail,
  Send,
  Search,
  Loader2,
  ArrowLeft,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [conversations, setConversations] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchInbox();
    
    // Check if we have a recipient from URL params
    const toUserId = searchParams.get('to');
    if (toUserId) {
      // Fetch user and start conversation
      fetchUserAndStartConversation(toUserId);
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchConversation(selectedUser.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchInbox = async () => {
    try {
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API}/messages/inbox`),
        axios.get(`${API}/messages/sent`)
      ]);
      
      // Group messages by conversation partner
      const allMessages = [...inboxRes.data, ...sentRes.data];
      const convos = {};
      
      allMessages.forEach(msg => {
        const partnerId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
        const partnerUsername = msg.sender_id === user.id ? msg.receiver_username : msg.sender_username;
        
        if (!convos[partnerId]) {
          convos[partnerId] = {
            id: partnerId,
            username: partnerUsername,
            lastMessage: msg,
            unread: msg.receiver_id === user.id && !msg.is_read
          };
        } else if (new Date(msg.created_at) > new Date(convos[partnerId].lastMessage.created_at)) {
          convos[partnerId].lastMessage = msg;
          if (msg.receiver_id === user.id && !msg.is_read) {
            convos[partnerId].unread = true;
          }
        }
      });
      
      setConversations(convos);
      setInbox(Object.values(convos).sort((a, b) => 
        new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
      ));
    } catch (error) {
      console.error('Error fetching inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAndStartConversation = async (userId) => {
    try {
      // Search for the user
      const response = await axios.get(`${API}/search/users?q=&limit=100`);
      const targetUser = response.data.find(u => u.id === userId);
      if (targetUser) {
        setSelectedUser(targetUser);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchConversation = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${userId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/search/users?q=${query}`);
      setSearchResults(response.data.filter(u => u.id !== user.id));
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        receiver_id: selectedUser.id,
        content: newMessage.trim()
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      fetchInbox(); // Refresh inbox
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = async (convo) => {
    // Get user profile for avatar
    try {
      const response = await axios.get(`${API}/users/${convo.username}`);
      setSelectedUser(response.data);
    } catch (error) {
      setSelectedUser({ id: convo.id, username: convo.username, display_name: convo.username });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-12rem)]">
          {/* Conversations List */}
          <Card className="lg:col-span-1 flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Messages
              </CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 input-field"
                  data-testid="message-search"
                />
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full">
                {searchQuery && searchResults.length > 0 ? (
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground px-2 py-1">Search Results</p>
                    {searchResults.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => {
                          setSelectedUser(u);
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                      >
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={u.avatar_url} />
                          <AvatarFallback>{u.display_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{u.display_name}</p>
                          <p className="text-sm text-muted-foreground">@{u.username}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : inbox.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <Mail className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No messages yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Search for users to start a conversation</p>
                  </div>
                ) : (
                  <div className="p-2">
                    {inbox.map((convo) => (
                      <button
                        key={convo.id}
                        onClick={() => selectConversation(convo)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                          selectedUser?.id === convo.id 
                            ? 'bg-primary/10' 
                            : 'hover:bg-muted'
                        }`}
                        data-testid={`conversation-${convo.id}`}
                      >
                        <Avatar className="w-10 h-10">
                          <AvatarFallback>{convo.username?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`font-medium ${convo.unread ? 'text-foreground' : ''}`}>
                              @{convo.username}
                            </p>
                            {convo.unread && (
                              <span className="w-2 h-2 rounded-full bg-secondary" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {convo.lastMessage.content}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-2 flex flex-col">
            {selectedUser ? (
              <>
                {/* Chat Header */}
                <CardHeader className="border-b pb-3">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="lg:hidden"
                      onClick={() => setSelectedUser(null)}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <Avatar 
                      className="w-10 h-10 cursor-pointer"
                      onClick={() => navigate(`/profile/${selectedUser.username}`)}
                    >
                      <AvatarImage src={selectedUser.avatar_url} />
                      <AvatarFallback>{selectedUser.display_name?.charAt(0) || selectedUser.username?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{selectedUser.display_name || selectedUser.username}</p>
                      <p className="text-sm text-muted-foreground">@{selectedUser.username}</p>
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full p-4">
                    {messages.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        <p>Start the conversation!</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`message-bubble ${msg.sender_id === user.id ? 'sent' : 'received'}`}
                            >
                              <p>{msg.content}</p>
                              <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>

                {/* Input */}
                <div className="p-4 border-t">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="input-field flex-1"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      className="btn-primary"
                      disabled={sending || !newMessage.trim()}
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <User className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Select a conversation or search for users</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Messages;
